import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ReactFlowProvider } from "reactflow";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth } from "./firebase";
import Flow from "./App/flow";
import SignIn from "./SignIn";
import Homepage from "./Homepage";
import Admin from "./Admin";
import Focus365 from "./App/Focus365";
import "primereact/resources/themes/mira/theme.css";
import "./styles/focusmap-theme.css";
import "./index.css";
import { Colors } from "./styles/colors";
import { generateCssVariables } from "./styles/generateCssVariables";
import { TooltipProvider } from "./Components/TooltipProvider";
import { TooltipManager } from "./Components/TooltipManager";
import "./hooks/overrideLogs";
import usePageTracking from "./hooks/usePageTracking";
import { ProgressSpinner } from "primereact/progressspinner";
import { getUserDoc } from "./storage/storageProxy";
import { useStore } from "./App/store";
import { initAnalytics } from "./analytics";

import "./syncfusionLicense";
import PreviewBanner from "./Components/PreviewBanner";

const AppRoutes = ({ user }: { user: User | null }) => {
  usePageTracking(); // ✅ Now inside the Router context

  useEffect(() => {
    // Initialize both Google Analytics and Microsoft Clarity
    initAnalytics();
  }, []);

  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route
        path="/flow/:flowId"
        element={user ? <Flow /> : <Navigate to="/signin" />}
      />
      <Route
        path="/"
        element={user ? <Homepage /> : <Navigate to="/signin" />}
      />
      <Route
        path="/admin"
        element={user ? <Admin /> : <Navigate to="/signin" />}
      />
      <Route path="/Focus365" element={<Focus365 />} />
    </Routes>
  );
};

const MainApp = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const cssVariables = generateCssVariables(Colors);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `:root { ${cssVariables} }`;
    document.head.appendChild(style);
  }, [cssVariables]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const loadUserData = async () => {
        // console.log("Loading user data...", user);
        if (user) {
          const userDoc = await getUserDoc(user.uid, false);
          if (userDoc) {
            useStore
              .getState()
              .setUseLocalStorage(
                userDoc.useLocalData ? userDoc.useLocalData : false
              );
          }

          setUser(user);
          setIsAuthenticated(!!user);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
        setLoading(false);
      };
      loadUserData();
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="progress-overlay">
        <ProgressSpinner
          strokeWidth="8"
          aria-label="Loading the Home Page..."
        />
      </div>
    );
  }

  return (
    <Router>
      <PreviewBanner />
      <AppRoutes user={user} />
    </Router>
  );
};

// Wrap with ReactFlowProvider and TooltipProvider
ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  // <React.StrictMode>
  <ReactFlowProvider>
    <TooltipProvider>
      <TooltipManager />
      <MainApp />
    </TooltipProvider>
  </ReactFlowProvider>
  // </React.StrictMode>
);
